import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Tenant, Customer, tenantServices, customerServices } from '../lib/supabase';

const TenantDetail = () => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchTenantAndCustomer = async () => {
      if (!id) {
        setError('No tenant ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch tenant details
        const { data: tenantData, error: tenantError } = await tenantServices.getById(id);
        if (tenantError) throw tenantError;
        setTenant(tenantData);

        // Fetch associated customer
        if (tenantData?.company_id) {
          const { data: customerData, error: customerError } = await customerServices.getById(tenantData.company_id);
          if (customerError) throw customerError;
          setCustomer(customerData);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tenant details');
      } finally {
        setLoading(false);
      }
    };

    fetchTenantAndCustomer();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'at risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!tenant) return <div className="text-center p-4">Tenant not found</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/tenants" className="flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tenants
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1 className="text-2xl font-bold">{tenant.name}</h1>
            <span className={`px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusColor(tenant.status)}`}>
              {tenant.status}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Company</p>
              <p className="mt-1 text-lg font-semibold">
                {customer ? (
                  <Link to={`/customers/${customer.id}`} className="text-blue-600 hover:text-blue-800">
                    {customer.name}
                  </Link>
                ) : 'Unknown Company'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Health Score</p>
              <p className={`mt-1 text-lg font-semibold ${getHealthScoreColor(tenant.health_score)}`}>
                {tenant.health_score}%
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Created</p>
              <p className="mt-1">{tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Last Updated</p>
              <p className="mt-1">{tenant.updated_at ? new Date(tenant.updated_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>

          {/* Recent Activity Section */}
          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-medium mb-4">Recent Activity</h2>
            <div className="text-center text-gray-500 py-4">
              Activity tracking coming soon
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDetail;
